'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

interface RegisterFormData {
  email: string;
  password: string;
  phone: string;
}

interface RegisterFormProps {
  onSuccess: (email: string) => void;
}

const passwordRules = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One number', test: (p: string) => /\d/.test(p) },
];

export default function RegisterForm({ onSuccess }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({ mode: 'onBlur' });

  const watchedPassword = watch('password', '');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    // Backend integration point: POST /api/v1/auth/register
    await new Promise((r) => setTimeout(r, 1400));
    setIsLoading(false);
    onSuccess(data.email);
  };

  const passwordStrength = passwordRules.filter((r) => r.test(watchedPassword)).length;

  return (
    <div>
      <div className="mb-7">
        <h2 className="font-display text-2xl font-semibold text-foreground mb-1">Start your journey</h2>
        <p className="text-sm text-muted-foreground">Create your SAATH profile — it takes 2 minutes</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Email */}
        <div>
          <label htmlFor="reg-email" className="block text-sm font-semibold text-foreground mb-1.5">
            Email address
          </label>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            placeholder="your@email.com"
            className={`w-full h-11 px-4 rounded-xl border text-sm transition-all duration-150 outline-none bg-card placeholder:text-muted-foreground/50
              focus:border-primary focus:ring-2 focus:ring-primary/12
              ${errors.email ? 'border-error bg-error/4' : 'border-border hover:border-muted-foreground/40'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-error flex items-center gap-1">
              <AlertCircle size={11} /> {errors.email.message}
            </p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="reg-phone" className="block text-sm font-semibold text-foreground mb-1.5">
            Mobile number
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">We'll send a verification code — not shared publicly</p>
          <div className="flex gap-2">
            <div className="flex items-center justify-center h-11 px-3 rounded-xl border border-border bg-muted text-sm text-muted-foreground font-medium flex-shrink-0">
              +91
            </div>
            <input
              id="reg-phone"
              type="tel"
              autoComplete="tel"
              placeholder="9876543210"
              className={`flex-1 h-11 px-4 rounded-xl border text-sm transition-all duration-150 outline-none bg-card placeholder:text-muted-foreground/50
                focus:border-primary focus:ring-2 focus:ring-primary/12
                ${errors.phone ? 'border-error bg-error/4' : 'border-border hover:border-muted-foreground/40'}`}
              {...register('phone', {
                required: 'Mobile number is required',
                pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number' },
              })}
            />
          </div>
          {errors.phone && (
            <p className="mt-1.5 text-xs text-error flex items-center gap-1">
              <AlertCircle size={11} /> {errors.phone.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="reg-password" className="block text-sm font-semibold text-foreground mb-1.5">
            Create a password
          </label>
          <div className="relative">
            <input
              id="reg-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Min. 8 characters"
              className={`w-full h-11 px-4 pr-11 rounded-xl border text-sm transition-all duration-150 outline-none bg-card placeholder:text-muted-foreground/50
                focus:border-primary focus:ring-2 focus:ring-primary/12
                ${errors.password ? 'border-error bg-error/4' : 'border-border hover:border-muted-foreground/40'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*\d).{8,}$/,
                  message: 'Include at least one uppercase letter and one number',
                },
                onChange: (e) => setPasswordValue(e.target.value),
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {/* Password strength */}
          {watchedPassword.length > 0 && (
            <div className="mt-2.5 space-y-1.5 fade-in">
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <div
                    key={`strength-bar-${i}`}
                    className="flex-1 h-1 rounded-full transition-all duration-300"
                    style={{
                      background: i < passwordStrength
                        ? passwordStrength === 1 ? 'var(--error)' : passwordStrength === 2 ? 'var(--warning)' : 'var(--success)'
                        : 'var(--border)',
                    }}
                  />
                ))}
              </div>
              <div className="space-y-1">
                {passwordRules.map((rule) => (
                  <div key={`pwd-rule-${rule.label}`} className="flex items-center gap-1.5">
                    <CheckCircle
                      size={11}
                      className={rule.test(watchedPassword) ? 'text-success' : 'text-border'}
                    />
                    <span className={`text-2xs ${rule.test(watchedPassword) ? 'text-success' : 'text-muted-foreground'}`}>
                      {rule.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {errors.password && (
            <p className="mt-1.5 text-xs text-error flex items-center gap-1">
              <AlertCircle size={11} /> {errors.password.message}
            </p>
          )}
        </div>

        {/* Terms */}
        <p className="text-xs text-muted-foreground leading-relaxed">
          By creating an account you agree to our{' '}
          <span className="text-primary font-medium cursor-pointer hover:underline">Terms of Service</span>
          {' '}and{' '}
          <span className="text-primary font-medium cursor-pointer hover:underline">Privacy Policy</span>.
          We take your privacy seriously.
        </p>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-11 rounded-xl brand-gradient text-white text-sm font-semibold shadow-primary-custom btn-press disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Creating account…</span>
            </>
          ) : (
            'Create Your Profile'
          )}
        </button>
      </form>
    </div>
  );
}